<template>
  <div class="mt-3">
    <p class="f-600 required">
      {{ getCashReceiptsQuestions.members.label }}
    </p>
    <v-layout
      align-center
      wrap
    >
      <template v-for="(member, index) in items">
        <v-flex
          v-if="selected.includes(member.id)"
          :key="index"
          xs12
        >
          <v-layout>
            <v-flex
              class="mt-4 mr-2"
              :shrink="lgAndUp"
            >
              <span class="fw-600 f-2 text-dark">{{ indexNum(index) }}</span>
            </v-flex>
            <v-flex>
              <v-layout :wrap="$vuetify.breakpoint.mdAndDown">
                <v-flex
                  v-if="getCashReceiptsQuestions.members.hasOwnProperty('name')"
                  lg2
                  md6
                  :class="[{ 'pr-3': $vuetify.breakpoint.mdAndUp }]"
                >
                  <v-text-field
                    :label="getCashReceiptsQuestions.members.name.label"
                    :value="member.name"
                    disabled
                    class="personal-input_disabled"
                  />
                </v-flex>
                <v-flex
                  v-if="getCashReceiptsQuestions.members.hasOwnProperty('position')"
                  lg3
                  md6
                  :class="[{ 'px-3': $vuetify.breakpoint.lgAndUp }]"
                >
                  <v-text-field
                    v-model.trim="member.position"
                    v-validate="{
                      ...requiredRegularField(getCashReceiptsQuestions.members.position.required)
                    }"
                    :label="getCashReceiptsQuestions.members.position.label"
                    :error-messages="
                      errors.collect(createDataName('active_members_position', member.id))
                    "
                    :data-vv-as="$t('sheet.sheetOne.meeting.cash_receipts.labels.position')"
                    :data-vv-name="createDataName('active_members_position', member.id)"
                    :required="getCashReceiptsQuestions.members.position.required"
                    :disabled="readOnly"
                    @blur="completePersonData(index)"
                    @focus="falseComplete(member.complete)"
                  />
                </v-flex>
                <v-flex
                  v-if="getCashReceiptsQuestions.members.hasOwnProperty('dimension')"
                  lg3
                  md6
                  :class="[
                    { 'px-3': $vuetify.breakpoint.lgAndUp },
                    { 'pr-3': $vuetify.breakpoint.md }
                  ]"
                >
                  <v-select
                    v-model.trim="member.dimension"
                    v-validate="{ required: getCashReceiptsQuestions.members.dimension.required }"
                    :items="dimensionValues"
                    :label="getCashReceiptsQuestions.members.dimension.label"
                    :error-messages="
                      errors.collect(createDataName('active_member_dimension', member.id))
                    "
                    :data-vv-as="$t('sheet.sheetOne.meeting.cash_receipts.labels.dimension')"
                    :data-vv-name="createDataName('active_member_dimension', member.id)"
                    :required="getCashReceiptsQuestions.members.dimension.required"
                    item-value="id"
                    item-text="description"
                    append-icon="expand_more"
                    :disabled="readOnly"
                    class="select-hidden-input"
                    @blur="completePersonData(member.id)"
                    @focus="falseComplete(member.complete)"
                  />
                </v-flex>
                <v-flex
                  v-if="getCashReceiptsQuestions.members.hasOwnProperty('income')"
                  lg4
                  md6
                  :class="[{ 'px-3': $vuetify.breakpoint.lgAndUp }]"
                >
                  <v-layout align-center>
                    <v-flex lg8>
                      <v-text-field
                        v-model="member.income"
                        v-validate="{
                          ...requiredMoney(getCashReceiptsQuestions.members.income.required)
                        }"
                        :label="getCashReceiptsQuestions.members.income.label"
                        :error-messages="
                          errors.collect(createDataName('active_members_amount', member.id))
                        "
                        :data-vv-as="$t('sheet.sheetOne.meeting.cash_receipts.labels.amount')"
                        :data-vv-name="createDataName('active_members_amount', member.id)"
                        :required="getCashReceiptsQuestions.members.income.required"
                        :disabled="readOnly"
                        inputmode="decimal"
                        @keyup="count"
                        @blur="completePersonData(member.id)"
                        @focus="falseComplete(member.complete)"
                      />
                    </v-flex>
                    <v-flex
                      lg4
                      shrink
                      justify-center
                    >
                      <v-subheader class="px-0 pt-1 ml-2">
                        {{ $t('label.amount_suffix') }}
                      </v-subheader>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-divider
            v-if="index < items.length - 1"
            class="my-3"
          />
        </v-flex>
      </template>
    </v-layout>
  </div>
</template>

<script>
import formatDecimalVariables from '@/mixins/formatDecimalVariables';
import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';
import sheetResourcesGetter from '@/mixins/sheet/sheetResourcesGetter';

import { mapGetters } from 'vuex';

export default {
    name: 'ActiveMembersForm',
    mixins: [formatDecimalVariables, sheetValidationRules, sheetResourcesGetter],
    inject: ['$validator'],
    props: {
        items: {
            type: Array,
            default: () => {
                return [];
            }
        },
        selected: {
            type: Array,
            default: () => {
                return [];
            }
        },
        readOnly: {
            default: false,
            type: Boolean
        }
    },
    computed: {
        ...mapGetters('sheet', ['getCashReceiptsQuestions']),
        lgAndUp () {
            return this.$vuetify.breakpoint.lgAndUp;
        },
        dimensionValues () {
            return this.getResources(this.getCashReceiptsQuestions.members.dimension);
        }
    },
    watch: {
        selected: {
            handler (val) {
                this.items.forEach((e) => {
                    if (!val.includes(e.id)) {
                        e.income = 0;
                    }
                });
                this.count();
            },
            immediate: true
        }
    },
    methods: {
        indexNum (index) {
            return index + 1 + '.';
        },
        createDataName (name, id) {
            return name + id;
        },
        completePersonData (id) {
            this.items.forEach((el) => {
                if (el.id === id) {
                    const _keys = [];
                    Object.keys(this.fields).forEach((key) => {
                        if (key.indexOf(id) !== -1) {
                            _keys.push(this.fields[key]);
                        }
                    });
                    el.complete = Object.keys(_keys).every((key) => _keys[key].valid);
                }
            });
        },
        falseComplete (el) {
            el = false;
        },
        count () {
            let sum = 0;
            this.items.forEach((e) => {
                if (
                    e.income != null &&
          !isNaN(this.formatDecimalVariable(e.income)) &&
          this.formatDecimalVariable(e.income) > 0
                ) {
                    sum += this.formatDecimalVariable(e.income);
                }
            });
            this.$emit('input', parseFloat(sum.toFixed(2)));
        }
    }
};
</script>

<style scoped></style>
